import { doorRules, type ProductRow } from '@planner/config';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { track } from '../lib/analytics/index.js';
import { completeStep } from '../lib/analytics/planner.js';
import { useState, type FormEvent } from 'react';
import { CatalogueNotice } from '../components/CatalogueNotice.js';
import { HandleMeasurementHelp } from '../components/HandleMeasurementHelp.js';
import { InfoTip } from '../components/InfoTip.js';
import { LockLayoutHelp } from '../components/LockLayoutHelp.js';
import {
  DesktopDoorPreview,
  MobileDoorPreview,
  type DoorPreviewPrice,
} from '../components/DoorPreview.js';
import { LockSideCard } from '../components/LockSideCard.js';
import { StepNavigation, StepProgress } from '../components/StepProgress.js';
import { estimateDoorPrice } from '../lib/catalogue.js';
import { evaluateLock, type LockHeightError } from '../lib/lock.js';
import {
  evaluateMeasurements,
  type CatalogueState,
  type MeasurementsEvaluation,
} from '../lib/measurements.js';
import { useNumberInput } from '../lib/useNumberInput.js';
import { useDoorStore, type DoorDraft } from '../stores/doorStore.js';

const heightMessages: Record<LockHeightError, string> = {
  required: 'Enter a handle height.',
  invalid: 'Enter a whole number of millimetres.',
  'out-of-range': `Handle height must be between ${doorRules.lockHeight.min} and ${doorRules.lockHeight.max} mm.`,
  clearance:
    'This handle position is incompatible with the finished door size. Change the handle height or return to Measurements to change the door size.',
};

export function LockHandleStep({
  draft,
  measurements,
  catalogue,
  products,
  retry,
}: {
  draft: DoorDraft;
  measurements: MeasurementsEvaluation;
  catalogue: CatalogueState;
  products: readonly ProductRow[] | undefined;
  retry(): void;
}) {
  const navigate = useNavigate();
  const router = useRouter();
  const setHeight = useDoorStore((state) => state.setLockHeight);
  const setSide = useDoorStore((state) => state.setLockSide);
  const [heightTouched, setHeightTouched] = useState(false);
  const [sideTouched, setSideTouched] = useState(false);
  const { badInput, cannotDisplay, transferNotice, inputProps } = useNumberInput(
    draft.lock.height,
    setHeight,
  );
  const lock = evaluateLock(draft.lock, draft.doorType, measurements);
  const previewPrice: DoorPreviewPrice =
    catalogue.status === 'ready'
      ? products && draft.doorType && measurements.size
        ? estimateDoorPrice(products, draft.doorType, measurements.size)
        : { status: 'unavailable' }
      : { status: catalogue.status === 'loading' ? 'checking' : 'unverified' };
  const heightError = badInput && lock.heightError === 'required' ? 'invalid' : lock.heightError;
  const showHeightError =
    cannotDisplay ||
    (heightError && (heightTouched || (draft.lock.height.trim() && heightError !== 'required')));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const current = useDoorStore.getState().draft;
    const currentMeasurements = evaluateMeasurements(current, catalogue);
    if (evaluateLock(current.lock, current.doorType, currentMeasurements).canContinue) {
      void completeStep(
        'lock',
        () => navigate({ to: '/planner/colour' }),
        () => router.state.location.pathname === '/planner/colour',
      );
    }
  };

  return (
    <>
      <StepProgress currentIndex={2} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-28 sm:py-10 lg:pb-10">
        <h1 className="mb-1 text-2xl font-semibold text-gray-900 sm:text-3xl">Handle position</h1>
        <p className="mb-7 text-sm text-gray-500">
          {draft.doorType === 'security'
            ? 'Security doors use a triple-lock system. Enter your preferred handle height.'
            : "Choose where you'd like the handle placed. Ensure the placement won't interfere or catch on your main door handle or other features."}
        </p>
        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
                {draft.doorType === 'security' && (
                  <div className="mb-5 flex items-center gap-3 border-b border-gray-100 pb-5">
                    <span
                      aria-hidden="true"
                      className="flex size-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600"
                    >
                      <svg
                        className="size-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect x="5" y="10" width="14" height="11" rx="2" />
                        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                      </svg>
                    </span>
                    <span className="text-sm font-semibold text-gray-800">Triple-lock system</span>
                    <InfoTip label="About the triple-lock system" name="lock-system-info">
                      Three locking points are operated together by the handle and key.
                    </InfoTip>
                  </div>
                )}
                <label
                  htmlFor="lock-height"
                  className="text-xs font-semibold tracking-wider text-gray-500 uppercase"
                >
                  Handle height
                </label>
                <p id="lock-height-hint" className="mt-1 mb-3 text-xs text-gray-400">
                  Required. We recommend {doorRules.recommendedLockHeightMm}mm, but you can move the
                  handle higher or lower (from {doorRules.lockHeight.min}mm up to{' '}
                  {doorRules.lockHeight.max}mm) to prevent it from catching on anything.
                </p>
                <div className="flex items-center gap-2">
                  <div className="relative w-full max-w-[180px] sm:w-40">
                    <input
                      id="lock-height"
                      data-name="lock-height"
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      aria-label="Handle height in millimetres"
                      aria-required="true"
                      aria-invalid={Boolean(showHeightError)}
                      aria-describedby={`lock-height-hint${showHeightError ? ' lock-height-error' : ''}${transferNotice ? ' lock-height-transfer-notice' : ''}`}
                      {...inputProps}
                      onBlur={() => {
                        setHeightTouched(true);
                        track('configuration_changed', { field: 'handle_height' });
                      }}
                      className="min-h-12 w-full rounded-lg border border-gray-200 py-2 pr-11 pl-3 text-base text-gray-700 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-100 sm:text-sm"
                    />
                    <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-gray-400">
                      mm
                    </span>
                  </div>
                  <HandleMeasurementHelp />
                </div>
                {showHeightError && heightError && (
                  <p id="lock-height-error" role="alert" className="mt-2 text-xs text-red-600">
                    {cannotDisplay ? (
                      <>
                        Your saved entry{' '}
                        <span className="break-all whitespace-pre-wrap">
                          &ldquo;{draft.lock.height}&rdquo;
                        </span>{' '}
                        cannot be shown in a number field. Enter a whole number of millimetres.
                      </>
                    ) : (
                      heightMessages[heightError]
                    )}
                  </p>
                )}
                {transferNotice && (
                  <p
                    id="lock-height-transfer-notice"
                    role="status"
                    className="mt-2 text-xs text-red-600"
                  >
                    Paste or drop digits only. Select the whole handle height to replace it.
                  </p>
                )}
                {draft.doorType && <LockLayoutHelp doorType={draft.doorType} />}
                <fieldset
                  className="mt-5 border-t border-gray-100 pt-5"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setSideTouched(true);
                    }
                  }}
                >
                  <legend className="sr-only">Which side do you want the handle on?</legend>
                  <p className="text-sm font-semibold text-gray-800">
                    Which side do you want the handle on?
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Stand outside your home and face the door. The door will swing outwards towards
                    you.
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {(['left', 'right'] as const).map((side) => (
                      <LockSideCard
                        key={side}
                        side={side}
                        selected={draft.lock.side === side}
                        invalid={sideTouched && Boolean(lock.sideError)}
                        onSelect={(side) => {
                          if (draft.lock.side !== side) {
                            setSide(side);
                            track('configuration_changed', { field: 'lock_side', value: side });
                          }
                        }}
                      />
                    ))}
                  </div>
                </fieldset>
                <CatalogueNotice status={catalogue.status} retry={retry} step="lock" />
              </div>
              {draft.doorType && measurements.size && (
                <MobileDoorPreview
                  step="lock"
                  doorType={draft.doorType}
                  frameColour={draft.frameColour}
                  size={measurements.size}
                  lock={draft.lock}
                  evaluation={lock}
                  price={previewPrice}
                  depth={draft.measurements.depth}
                />
              )}
              <StepNavigation currentIndex={2} canContinue={lock.canContinue} submit mobileSticky />
            </div>
            {draft.doorType && measurements.size && (
              <DesktopDoorPreview
                step="lock"
                doorType={draft.doorType}
                frameColour={draft.frameColour}
                size={measurements.size}
                lock={draft.lock}
                evaluation={lock}
                price={previewPrice}
              />
            )}
          </div>
        </form>
      </main>
    </>
  );
}
